import { CheckboxProps } from 'antd'
import { TableRowSelection } from 'antd/es/table/interface'
import { useCallback, useMemo, useState } from 'react'

export interface UseTableRowSelectionOptions<T> {
  limit?: number
  max?: number
  preserveSelectedRowKeys?: boolean
  onChange?: (keys: React.Key[], rows: T[]) => void
  initialSelectedRowKeys?: React.Key[]
  getCheckboxProps?: (record: T) => Partial<CheckboxProps>
  selectionType?: 'checkbox' | 'radio'
  isKeyEqual?: (a: React.Key, b: React.Key) => boolean
  getRowKey?: (record: T) => React.Key
}

export interface UseTableRowSelectionResult<T> {
  selectedRowKeys: React.Key[]
  selectedRows: T[]
  rowSelection: TableRowSelection<T>
  resetSelection: () => void
  selectAll: (rows: T[]) => void
  selectByPredicate: (rows: T[], predicate: (r: T) => boolean) => void
  toggleKey: (key: React.Key, row?: T) => void
  isSelected: (key: React.Key) => boolean
  selectedCount: number
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>
  setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>
}

export function useTableRowSelection<T>(
  options: UseTableRowSelectionOptions<T> = {}
): UseTableRowSelectionResult<T> {
  const {
    limit,
    max,
    preserveSelectedRowKeys = false,
    onChange,
    initialSelectedRowKeys = [],
    getCheckboxProps,
    selectionType = 'checkbox',
    isKeyEqual = (a, b) => a === b,
    getRowKey = (row: T) => {
      const rowKeySource = row as unknown as { id?: React.Key; key?: React.Key }
      return (rowKeySource.id ?? rowKeySource.key) as React.Key
    },
  } = options

  const maxAllowed = limit ?? max

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    initialSelectedRowKeys
  )
  const [selectedRows, setSelectedRows] = useState<T[]>([])

  const resetSelection = useCallback(() => {
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  const internalOnChange = useCallback(
    (keys: React.Key[], rows: T[]) => {
      if (maxAllowed && keys.length > maxAllowed) return
      setSelectedRowKeys(keys)
      setSelectedRows(rows)
      onChange?.(keys, rows)
    },
    [maxAllowed, onChange]
  )

  const isSelected = useCallback(
    (key: React.Key) =>
      selectedRowKeys.some(selectedRowKey => isKeyEqual(selectedRowKey, key)),
    [isKeyEqual, selectedRowKeys]
  )

  const toggleKey = useCallback(
    (key: React.Key, row?: T) => {
      setSelectedRowKeys(previousSelectedKeys => {
        const exists = previousSelectedKeys.some(prevKey =>
          isKeyEqual(prevKey, key)
        )
        if (exists) {
          setSelectedRows(previousSelectedRows =>
            previousSelectedRows.filter(
              item => !isKeyEqual(getRowKey(item), key)
            )
          )
          return previousSelectedKeys.filter(
            selectedRowKey => !isKeyEqual(selectedRowKey, key)
          )
        } else {
          if (maxAllowed && previousSelectedKeys.length >= maxAllowed)
            return previousSelectedKeys
          if (row)
            setSelectedRows(previousSelectedRows => [
              ...previousSelectedRows,
              row,
            ])
          return [...previousSelectedKeys, key]
        }
      })
    },
    [getRowKey, isKeyEqual, maxAllowed]
  )

  const selectAll = useCallback(
    (rows: T[]) => {
      const keys = rows.map(row => getRowKey(row))
      const limited = maxAllowed ? keys.slice(0, maxAllowed) : keys
      const limitedRows = maxAllowed ? rows.slice(0, maxAllowed) : rows
      setSelectedRowKeys(limited)
      setSelectedRows(limitedRows)
    },
    [getRowKey, maxAllowed]
  )

  const selectByPredicate = useCallback(
    (rows: T[], predicate: (row: T) => boolean) => {
      const filtered = rows.filter(predicate)
      selectAll(filtered)
    },
    [selectAll]
  )

  const rowSelection: TableRowSelection<T> = useMemo(
    () => ({
      selectedRowKeys,
      onChange: internalOnChange,
      preserveSelectedRowKeys,
      type: selectionType,
      getCheckboxProps: getCheckboxProps
        ? (record: T) => getCheckboxProps(record) as CheckboxProps
        : undefined,
    }),
    [
      getCheckboxProps,
      internalOnChange,
      preserveSelectedRowKeys,
      selectedRowKeys,
      selectionType,
    ]
  )

  return {
    selectedRowKeys,
    selectedRows,
    rowSelection,
    resetSelection,
    selectAll,
    selectByPredicate,
    toggleKey,
    isSelected,
    selectedCount: selectedRowKeys.length,
    setSelectedRowKeys,
    setSelectedRows,
  }
}
